import { useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Container,
  Button,
  IconButton,
  Chip,
  Card,
  FormControlLabel,
  Checkbox
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import ACL from 'components/ACL';
import Breadcrumbs from 'components/Breadcrumbs';
import List from 'components/List2';
import Iconify from 'components/Iconify';
import { UserContext } from 'contexts';

import { cancelContract, getContractsProvider } from 'services/requests/contract';
import { toast, setMask } from 'utils';
import { useDialog } from 'components/Dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import ContractForm from '../ContractForm';

dayjs.extend(utc);

export default function ContractsProvider() {
  const { t } = useTranslation('common');
  const listRef = useRef(null);
  const userContext = useContext(UserContext);

  const dialog = useDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()

  const [newContract, setNewContract] = useState(false);
  const [statusClosed, setStatusClosed] = useState(null);

  const searchFromUrlPSellerId = searchParams.get('seller_id') || undefined
  const searchFromUrlPending = searchParams.get('pending') || undefined

  const userRole = userContext?.state?.data?.role;
  const isCompanyActive = userRole === 'admin'
    ? userContext?.state?.data?.company?.active !== false
    : userContext?.state?.data?.provider?.active !== false;

  const listConfig = {
    placeholderFilter: t('contracts.search'),
    onClick: row => navigate(`/contratos/todos/detalhe/${row.id}`),
    fields: [
      { key: 'number', label: 'Nº', desktopColumnSize: 1, isPrimary: true },
      { key: 'name', label: 'Cliente', desktopColumnSize: 2 },
      { key: 'goal', label: 'Objetivo', desktopColumnSize: 2, sm_prefix: 'Objetivo: ' },
      { key: 'amount', label: 'Valor pactuado', desktopColumnSize: 2, customValue: row => setMask('money', row.amount) },
      { key: 'installments', label: 'Parcelas', desktopColumnSize: 2, customValue: row => row.installments },
      {
        key: 'status', label: 'Status', desktopColumnSize: 3, customValue: row => (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row">
              <Chip label={row.statusLabel} size="small" variant="outlined" color={row.statusColor} />
            </Stack>
            <ACL profiles={['admin', 'seller']}>
              {
                (row.status !== 'borrowed' && row.status !== 'finished') ? (
                  <IconButton sx={{ color: 'error.main', ml: 1 }} onClick={e => deleteItem(e, row)}>
                    <Iconify icon="ic:outline-block" />
                  </IconButton>
                ) : null
              }
            </ACL>
          </Stack>
        )
      }
    ]
  }

  const deleteItem = (e, contract) => {
    e.stopPropagation()

    dialog({
      title: 'Cancelar contrato',
      description: `Deseja realmente cancelar o contrato de ${contract.name} no valor de ${setMask('money', contract.amount)}?`,
    })
      .then(() => {
        cancelContract(contract.id)
          .then(() => {
            toast('Contrato cancelado com sucesso!', 'success')
            if (listRef.current) {
              listRef.current.refreshData()
            }
          }).catch(err => {
            toast(err, 'error')
          })
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (listRef?.current && statusClosed !== null) {
      listRef.current.refreshData()
    }
  }, [statusClosed])

  return (
    <>
      <Helmet>
        <title> {t('contractsProvider.pageTitle')} | { process.env.REACT_APP_PROJECT_NAME } </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Breadcrumbs
            items={[
              { label: t('contractsProvider.title') }
            ]}
          />

          <ACL profiles={['admin', 'seller']}>
            {isCompanyActive && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => setNewContract(true)}
              >
                {t('contracts.new')}
              </Button>
            )}
          </ACL>
        </Stack>

        <Card sx={{ px: 3, py: 1, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="status"
                onChange={e => setStatusClosed(e.target.checked)}
              />
            }
            label="Mostrar contratos finalizados e cancelados"
          />
        </Card>

        <List
          config={listConfig}
          getData={(params) => getContractsProvider({
            ...params,
            closed: statusClosed,
            sellerId: searchFromUrlPSellerId,
            pending: searchFromUrlPending
          })}
          parseData={({ id, number, name, goal, amount, installmentscount, installmentspayed, status }) => ({
            id, number, name, goal, amount, status,
            installments: `Pagas ${installmentspayed}/${installmentscount}`,
            statusLabel: t(`contracts.status.${status}`),
            statusColor: status === 'created' ? 'info' : status === 'pendingSign' || status === 'signed' || status === 'borrowed' ? 'warning' : status === 'cancelled' ? 'error' : 'success'
          })}
          ref={listRef}
        />

        <ContractForm
          open={newContract}
          onCancel={() => setNewContract(false)}
        />
      </Container>
    </>
  )
}
